import { ArrowRight01Icon, CompassIcon } from '@hugeicons/react'
import clsx from 'clsx'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { STATUS_LABELS as CANONICAL_STATUS_LABELS } from '@/shared/lib/status'
import { Badge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { SectionHeader } from '@/shared/ui/Layout'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { RewardBadge } from '@/shared/ui/RewardBadge'

import type { FeaturedQuest, NextMissionItem } from '../hooks/useHomeView'

/* Cópia local do vocabulário removida: era a quinta tabela de rótulos de status
   do app, e divergia das outras quatro. Vem do canônico. */
const STATUS_LABELS = CANONICAL_STATUS_LABELS

const FeaturedQuestRow = ({ featured }: { featured: FeaturedQuest }) => {
  const { navigateTo } = useNavigation()

  return (
    <button
      type="button"
      onClick={() => navigateTo(featured.to)}
      className="flex w-full cursor-pointer flex-col gap-2 border-0 bg-transparent py-0 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
    >
      <div className="flex w-full items-center gap-3">
        <span className="block h-14 w-14 shrink-0 overflow-hidden rounded-[0.625rem] bg-brand-surface">
          {featured.imageUrl ? (
            <img
              src={featured.imageUrl}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center">
              <CompassIcon
                size={22}
                variant="duotone"
                className="text-[var(--color-brand)] opacity-50"
                aria-hidden
              />
            </span>
          )}
        </span>

        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {/* `available` sem badge — status-padrão, tag só pra exceção. */}
          {featured.status !== 'available' && (
            <span className="flex items-center gap-2">
              <Badge variant={featured.status} size="sm" showIcon={false}>
                {STATUS_LABELS[featured.status]}
              </Badge>
            </span>
          )}
          <span className="line-clamp-1 text-sm font-bold leading-snug text-gray-900">
            {featured.title}
          </span>
          {(featured.pointReward > 0 || featured.coinReward > 0) && (
            <RewardBadge.Group>
              {featured.pointReward > 0 && (
                <RewardBadge
                  type="xp"
                  value={featured.pointReward}
                  variant="inline"
                />
              )}
              {featured.coinReward > 0 && (
                <RewardBadge
                  type="coins"
                  value={featured.coinReward}
                  variant="inline"
                />
              )}
            </RewardBadge.Group>
          )}
        </span>

        <Button
          variant={featured.status === 'progress' ? 'progress' : 'primary'}
          size="sm"
          className="shrink-0 font-bold"
          onClick={event => {
            event.stopPropagation()
            navigateTo(featured.to)
          }}
        >
          {featured.ctaLabel}
        </Button>
      </div>

      <div className="flex w-full items-center gap-2">
        <ProgressBar
          value={featured.percent}
          variant={featured.status === 'progress' ? 'progress' : 'primary'}
          size="sm"
          className="min-w-0 flex-1"
        />
        <span className="shrink-0 text-2xs font-bold leading-none text-gray-700">
          {featured.percent}%
        </span>
      </div>
    </button>
  )
}

const NextMissionRow = ({ mission }: { mission: NextMissionItem }) => {
  const { navigateTo } = useNavigation()

  return (
    <button
      type="button"
      onClick={() => navigateTo(mission.to)}
      className="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-2 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-center gap-2">
          <span className="line-clamp-1 text-sm font-bold leading-snug text-gray-900">
            {mission.title}
          </span>
          {mission.isEndingSoon && (
            <span className="shrink-0 text-caps font-bold uppercase tracking-wide text-orange-600">
              Encerrando
            </span>
          )}
        </span>
        <span className="flex items-center gap-2">
          <ProgressBar
            value={mission.percent}
            variant={mission.status === 'progress' ? 'progress' : 'primary'}
            size="sm"
            className="min-w-0 flex-1"
          />
          <span className="shrink-0 text-2xs font-bold leading-none text-gray-700">
            {mission.percent}%
          </span>
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-2">
        {mission.pointReward > 0 && (
          <RewardBadge
            type="xp"
            value={mission.pointReward}
            variant="inline"
          />
        )}
        <ArrowRight01Icon size={16} className="text-gray-700" aria-hidden />
      </span>
    </button>
  )
}

type QuestBookCardProps = {
  featured: FeaturedQuest | null
  nextMissions: NextMissionItem[]
  journeysProgress: { completed: number; total: number; percent: number }
  missionsTotal: number
  hasJourneys: boolean
}

export const QuestBookCard = ({
  featured,
  nextMissions,
  journeysProgress,
  missionsTotal,
  hasJourneys
}: QuestBookCardProps) => {
  const { navigateTo } = useNavigation()
  const { digitalProduct } = useDigitalProductContext()
  const isEmpty = !featured && nextMissions.length === 0
  const eventName = digitalProduct?.name ?? 'Seu evento'
  const journeySubtitle =
    journeysProgress.total > 0
      ? `${journeysProgress.total} jornada${journeysProgress.total === 1 ? '' : 's'}${missionsTotal > 0 ? ` · ${missionsTotal} miss${missionsTotal === 1 ? 'ão' : 'ões'}` : ''}`
      : missionsTotal > 0
        ? `${missionsTotal} miss${missionsTotal === 1 ? 'ão' : 'ões'}`
        : null

  return (
    <Card className="card-gamification overflow-hidden">
      {/* Header — identidade PageHero em faixa compacta */}
      <div
        className="px-4 pb-3 pt-3 text-white"
        style={{ background: 'var(--gradient-hero)' }}
      >
        <SectionHeader
          tone="inverse"
          icon={<CompassIcon size={14} variant="stroke" aria-hidden />}
          title="Sua jornada"
        />
        <div className="mt-1 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="m-0 text-base font-bold leading-tight">
              {hasJourneys ? eventName : 'Comece a ganhar experiência'}
            </p>
            {hasJourneys && journeySubtitle && (
              <p className="m-0 mt-0.5 text-2xs text-white/65">
                {journeySubtitle}
              </p>
            )}
          </div>
          {hasJourneys && (
            <span className="shrink-0 text-2xl font-extrabold leading-none">
              {journeysProgress.percent}%
            </span>
          )}
        </div>
        {hasJourneys && (
          <ProgressBar
            value={journeysProgress.percent}
            variant="primary"
            size="sm"
            className="mt-2 [&>div]:bg-white/20 [&>div]:shadow-none"
          />
        )}
        <div className="mt-2.5">
          <Button
            variant="light"
            size="sm"
            className="shrink-0 !text-[var(--color-brand)] font-bold"
            onClick={() => navigateTo('/jornadas')}
            trailingIcon={
              <ArrowRight01Icon size={14} variant="stroke" aria-hidden />
            }
          >
            Ver jornadas
          </Button>
        </div>
      </div>

      {isEmpty ? (
        <div className="flex flex-col items-center gap-2 px-4 py-5 text-center">
          <p className="m-0 text-sm font-bold text-gray-900">
            Nenhuma jornada disponível agora
          </p>
          <Button
            variant="primary"
            size="sm"
            className="font-bold"
            onClick={() => navigateTo('/jornadas')}
          >
            Explorar evento
          </Button>
        </div>
      ) : (
        <div className="flex flex-col">
          {featured && (
            <div className="border-b border-gray-100 px-4 pb-3 pt-3">
              <p className="m-0 mb-2 text-caps font-bold uppercase tracking-[0.04em] text-gray-700">
                Missão em destaque
              </p>
              <FeaturedQuestRow featured={featured} />
            </div>
          )}

          {nextMissions.length > 0 && (
            <div
              className={clsx(
                'flex flex-col divide-y divide-gray-100',
                featured && 'border-t border-gray-100'
              )}
            >
              {nextMissions.map(mission => (
                <NextMissionRow key={mission.key} mission={mission} />
              ))}
            </div>
          )}
        </div>
      )}

      {!isEmpty && (
        <button
          type="button"
          onClick={() => navigateTo('/jornadas')}
          className="flex w-full cursor-pointer items-center justify-center gap-1 border-0 border-t border-solid border-gray-100 bg-transparent py-2 text-2xs font-bold text-[var(--color-brand)] transition-colors hover:bg-gray-50"
        >
          Ver todas as jornadas
          <ArrowRight01Icon size={13} aria-hidden />
        </button>
      )}
    </Card>
  )
}
